import React, { useState } from 'react';
import { styled } from '@mui/material/styles';
// import clsx from 'clsx';
import { MenuAction } from 'models/types';
import CssBaseline from '@mui/material/CssBaseline';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import UserNavbarHeader from 'components/UserNavbarHeader';
import SmeAppBar from 'containers/SmeAppBar';
import { drawerWidth } from 'kosmosConfig';
import { IMenuItem } from 'data/MenuConfig';
import SidebarItem from 'components/SidebarItem';
import Footer from 'kosmos/components/Footer';
import MenuConfigContext from 'data/MenuConfigContext';
import CurrentUser from 'auth/currentUser';
import Logo from '../../logos/Logo';
import { useTranslation } from 'react-i18next';

const PREFIX = 'AdminApp';

const classes = {
  root: `${PREFIX}-root`,
  toolbar: `${PREFIX}-toolbar`,
  appBar: `${PREFIX}-appBar`,
  appBarShift: `${PREFIX}-appBarShift`,
  menuButton: `${PREFIX}-menuButton`,
  menuButtonHidden: `${PREFIX}-menuButtonHidden`,
  title: `${PREFIX}-title`,
  appBarSpacer: `${PREFIX}-appBarSpacer`,
  dividerSpacer: `${PREFIX}-dividerSpacer`,
  contentContainer: `${PREFIX}-contentContainer`,
  content: `${PREFIX}-content`,
  container: `${PREFIX}-container`,
  paper: `${PREFIX}-paper`,
  fixedHeight: `${PREFIX}-fixedHeight`,
  drawerPaper: `${PREFIX}-drawerPaper`,
  fixedSidebar: `${PREFIX}-fixedSidebar`,
  toolbarIcon: `${PREFIX}-toolbarIcon`,
  logo: `${PREFIX}-logo`,
  sidebarHeader: `${PREFIX}-sidebarHeader`,
  sidebarBottomSpacer: `${PREFIX}-sidebarBottomSpacer`,
  list: `${PREFIX}-list`,
};

const Root = styled('div')(({ theme }) => ({
  [`&.${classes.root}`]: {
    display: 'flex',
  },

  [`& .${classes.toolbar}`]: {
    background: 'white',
    color: '#121212',
    paddingRight: 24, // keep right padding when drawer closed
  },

  [`& .${classes.appBar}`]: {
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },

  [`& .${classes.appBarShift}`]: {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },

  [`& .${classes.menuButton}`]: {
    marginRight: 36,
  },

  [`& .${classes.menuButtonHidden}`]: {
    display: 'none',
  },

  [`& .${classes.title}`]: {
    flexGrow: 1,
  },

  [`& .${classes.appBarSpacer}`]: theme.mixins.toolbar,
  [`& .${classes.dividerSpacer}`]: {
    height: 45,
    backgroundColor: theme.palette.primary.main,
  },

  [`& .${classes.contentContainer}`]: {
    minHeight: '100vh',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
  },

  [`& .${classes.content}`]: {
    flex: 1,
    display: 'flex',
  },

  [`& .${classes.container}`]: {
    position: 'relative',
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },

  [`& .${classes.paper}`]: {
    padding: theme.spacing(2),
    display: 'flex',
    overflow: 'auto',
    flexDirection: 'column',
  },

  [`& .${classes.fixedHeight}`]: {
    height: 240,
  },

  [`& .${classes.drawerPaper}`]: {
    backgroundColor: '#121212',
    border: 'none',
    position: 'relative',
    whiteSpace: 'nowrap',
    width: drawerWidth,
  },
  [`& .${classes.fixedSidebar}`]: {
    backgroundColor: '#121212',
    border: 'none',
    position: 'fixed',
    whiteSpace: 'nowrap',
    width: drawerWidth,
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },

  [`& .${classes.toolbarIcon}`]: {
    textAlign: 'center',
    padding: '8px',
    color: 'white',
    backgroundColor: 'transparent',
    ...theme.mixins.toolbar,
  },
  [`& .${classes.logo}`]: {
    margin: '0 auto',
  },
  [`& .${classes.sidebarHeader}`]: {
    zIndex: 1000,
    width: drawerWidth,
    position: 'relative',
    background: 'radial-gradient(circle, rgba(68,140,203,1) 0%, rgba(38,50,105,1) 100%)',
  },
  [`& .${classes.sidebarBottomSpacer}`]: {
    backgroundColor: '#121212',
    position: 'absolute',
    bottom: 0,
    height: 34,
    width: '100%',
  },
  [`& .${classes.list}`]: {
    flex: 1,
    color: 'white',
    overflowY: 'auto',
  },
}));

interface OnRender {
  (actions?: MenuAction[]): JSX.Element;
}

interface Props {
  onRenderTfp: OnRender;
  children: React.ReactNode;
}

export default function AdminApp(props: Props): JSX.Element {
  const { t } = useTranslation('default');
  const currentUser = CurrentUser.getInstance();
  const [role, setRole] = useState(currentUser.getRole() || 'admin');

  const menuList: IMenuItem[] = [
    {
      label: t('all-users'),
      path: '/users',
      menuId: 'users',
    },
    {
      label: t('all-organisations'),
      path: '/organisations',
      menuId: 'organisations',
    },
    {
      label: t('all-company-lists'),
      path: '/companyLists',
      menuId: 'company-lists',
    },
    {
      label: t('search-companies'),
      path: '/home',
      menuId: 'home',
    },
    // {
    //   label: 'Search TradesunAI',
    //   path: '/tradesun_ai',
    //   menuId: 'tradesun_ai',
    // },
    {
      label: t('all-scored-companies'),
      path: '/scored_companies',
      menuId: 'scored-companies',
    },
    {
      label: t('all-company-registrations'),
      path: '/wome_applications',
      menuId: 'wome_applications',
    },
    {
      label: t('all-scored-transactions'),
      path: '/transactions',
      menuId: 'transactions',
    },
    {
      label: 'All Product list',
      path: '/productLists',
      menuId: 'product_list',
    },
    {
      label: 'Temp Tasks',
      path: '/temp_tasks',
      menuId: 'temp-task',
    },
  ];

  const [menuItems, setMenuItems] = useState<IMenuItem[]>(menuList);

  const handleRoleChange = (role: string) => {
    currentUser.setRole(role);
    setRole(role);
    if (role === 'admin') {
      setMenuItems(menuList);
    }
  };

  const actions: MenuAction[] = [
    { label: t('admin-view'), onClick: () => handleRoleChange('admin') },
    { label: t('preview-tfp-view'), onClick: () => handleRoleChange('tfp') },
  ];

  const renderAdminView = () => (
    <Root className={classes.root}>
      <CssBaseline />
      <SmeAppBar
        hideNotification={true}
        open={true}
        title={t('admin-dashboard')}
        actions={actions}
      />
      <Drawer
        variant="permanent"
        classes={{
          paper: classes.drawerPaper,
        }}
        open={true}
      >
        <div className={classes.fixedSidebar}>
          <div className={classes.sidebarHeader}>
            <div className={classes.toolbarIcon}>
              <Logo className={classes.logo} height={75} color="white" alt="coriolisLogoWhite" />
            </div>
            <UserNavbarHeader open={true} transparent={true} />
            <div className={classes.sidebarBottomSpacer} />
          </div>
          <List dense={true} className={classes.list}>
            {menuItems.map((section) => (
              <SidebarItem
                key={section.label}
                label={section.label}
                icon={section.icon}
                path={section.path}
                minimise={false}
              />
            ))}
          </List>
          <Footer />
        </div>
      </Drawer>

      <main className={classes.contentContainer}>
        <div className={classes.appBarSpacer} />
        <div className={classes.dividerSpacer} />
        <div className={classes.content}>
          <MenuConfigContext.Provider
            value={{
              menuItems,
              setMenuItems,
            }}
          >
            {props.children}
          </MenuConfigContext.Provider>
        </div>
      </main>
    </Root>
  );

  return <div key={role}>{role === 'tfp' ? props.onRenderTfp(actions) : renderAdminView()}</div>;
}
